"use client";

import { Button } from "@/components/ui/button";
import { getSession, signIn } from "next-auth/react";
import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useAnalytics } from "@/hooks/useAnalytics";

import Cookies from "js-cookie";
import Image from "next/image";
import Link from "next/link";
import LoadingSpinner from "@/components/LoadingSpinner";
import { getWorkspaceBySubdomain } from "@/server-actions/workspaces/actions";
import googleLogo from "@/public/google-logo.png";
import ripplingLogo from "@/public/rippling-logo.png";
import logo from "@/public/logo-orange.png";
import { Eye, EyeOff } from "lucide-react";
import { getSafeCallbackUrl } from "@/lib/auth/safe-callback-url";

export default function SignInForm() {
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl");
  const errorMessage = searchParams.get("error");
  const { trackButtonClick, trackLogin } = useAnalytics();

  const [hostname, setHostname] = useState("");
  const [preSubdomain, setPreSubdomain] = useState("");

  useEffect(() => {
    // Access window only after component is mounted (client-side)
    const currentHostname = window.location.hostname;
    setHostname(currentHostname);
    setPreSubdomain(currentHostname.split(".salesably.ai")[0]);
  }, []);

  const subdomainParam = searchParams.get("subdomain") || preSubdomain;
  const twoFactorFailed = searchParams.get("2fa_failed") === "true";

  // Rippling SSO is currently provisioned for the Silver Assist University
  // studio only (single-tenant Rippling app). Show the SAML sign-in entry point
  // for that subdomain.
  const isRipplingWorkspace =
    subdomainParam === "silverassistuniversity" ||
    hostname.startsWith("silverassistuniversity.");

  const [email, setEmail] = useState(Cookies.get("userEmail") || "");
  const [password, setPassword] = useState(
    twoFactorFailed ? "" : Cookies.get("userPassword") || ""
  );
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState(() => {
    if (twoFactorFailed) {
      return "Two-factor verification was unsuccessful. Please sign in to try again.";
    }
    if (!errorMessage) return "";
    // Map NextAuth callback error codes/messages to user-friendly text
    const errorMap = {
      CredentialsSignin: "Invalid email or password. Please try again.",
      OAuthSignin: "Unable to sign in with this provider. Please try again.",
      OAuthCallback: "Authentication failed. Please try again.",
      OAuthCreateAccount: "Unable to create account with this provider.",
      EmailCreateAccount: "Unable to create account with this email.",
      Callback: "An authentication error occurred. Please try again.",
      OAuthAccountNotLinked: "This email is already associated with another sign-in method.",
      SessionRequired: "Please sign in to continue.",
      Default: "An error occurred during sign in. Please try again.",
    };
    return errorMap[errorMessage] || errorMap.Default;
  });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [displayLogo, setDisplayLogo] = useState(null);
  const [isLoadingWorkspace, setIsLoadingWorkspace] = useState(
    !!subdomainParam
  );
  const [showMicrosoftSignIn, setShowMicrosoftSignIn] = useState(false);
  const router = useRouter();

  useEffect(() => {
    async function fetchWorkspace() {
      // Get studio from URL or localStorage
      const subdomainToUse = subdomainParam;

      if (
        subdomainToUse &&
        subdomainToUse !== "app" &&
        subdomainToUse !== "dev" &&
        subdomainToUse !== "localhost"
      ) {
        setIsLoadingWorkspace(true);
        try {
          const result = await getWorkspaceBySubdomain(subdomainToUse);
          if (result.success && result.workspace) {
            setWorkspace(result.workspace);
            setDisplayName(result.workspace.name);
            if (result.workspace.image) {
              setDisplayLogo(result.workspace.image);
            }
          } else {
            // Handle case where subdomain is invalid
            setError(
              "Invalid studio subdomain. Please check the URL and try again."
            );
          }
        } catch (err) {
          console.error("Error fetching workspace:", err);
          setError("Error loading studio. Please try again later.");
        } finally {
          setIsLoadingWorkspace(false);
        }
      } else {
        // If no valid subdomain, don't fetch and ensure loading state is false
        setIsLoadingWorkspace(false);
      }
    }

    fetchWorkspace();
  }, [subdomainParam]);

  useEffect(() => {
    // Get the hostname from the window object
    const hostname = window.location.hostname;

    // Check for subdomain in URL params first
    const subdomainToCheck = subdomainParam || "";

    // Show Microsoft sign-in if subdomain is 'primrose' (either from URL or hostname)
    if (subdomainToCheck === "primrose" || hostname.startsWith("primrose.")) {
      setShowMicrosoftSignIn(true);
      handleMicrosoftSignIn();
    }
  }, [subdomainParam]);

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleGoogleSignIn = async () => {
    trackButtonClick("google_sign_in", { method: "google" });
    await signIn("google", {
      callbackUrl: callbackUrl || "/",
    });
  };

  const handleMicrosoftSignIn = async () => {
    await signIn("azure-ad", {
      callbackUrl: callbackUrl || "/",
    });
  };

  const handleRipplingSignIn = () => {
    trackButtonClick("rippling_sign_in", { method: "rippling" });
    // Rippling is SP-initiated SAML: hand off to the start route, which builds
    // the relay state and redirects to Rippling's IdP. Full navigation (not
    // next-auth signIn) because the flow lives in our /api/auth/saml routes.
    const returnTo = callbackUrl || "/";
    // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- Authentication endpoints require a document request to follow the provider redirect.
    window.location.href = `/api/auth/saml/rippling/start?returnTo=${encodeURIComponent(
      returnTo
    )}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    trackButtonClick("sign_in_submit", { method: "email" });
    setMessage("");
    setError("");

    if (rememberMe) {
      // Ideally, save a secure token, not the actual email or password
      Cookies.set("userEmail", email, { expires: 7 });
      Cookies.set("userPassword", password, { expires: 7 });
    } else {
      // Ensure any existing cookies are cleared if "Remember Me" is not checked
      Cookies.remove("userEmail");
      Cookies.remove("userPassword");
    }
    try {
      const result = await signIn("credentials", {
        redirect: false,
        email,
        password,
        lastActive: new Date().toISOString(),
        callbackUrl: callbackUrl || "/",
      });

      if (result?.error) {
        setError(result.error);
        return;
      }

      if (result?.ok) {
        trackLogin("email");
        setMessage("Redirecting...");
        const session = await getSession();
        if (session?.user?.initialSignIn) {
          const resetParams = new URLSearchParams({
            forceInitial: "true",
            callbackUrl: getSafeCallbackUrl(
              result.url || callbackUrl || "/",
              "/",
              window.location.origin
            ),
          });
          router.push(`/reset-password?${resetParams.toString()}`);
          return;
        }
        const destination = result.url || callbackUrl || "/";
        router.push(destination);
        router.refresh();
        return;
      }

      setError("Unable to sign in. Please try again.");
    } catch (error) {
      console.error("An unexpected error occurred:", error);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (isLoadingWorkspace) {
    return (
      <div className="mx-auto max-w-7xl">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      <div className="relative flex items-center justify-center">
        <div className="max-w-md mx-auto shadow-md">
          <div className="h-full p-10 bg-white">
            <div className="max-w-md mx-auto">
              <div className="mb-4">
                <div className="flex items-end mb-10 text-2xl font-bold text-gray-900">
                  {displayLogo ? (
                    <Image
                      src={displayLogo}
                      alt={`${displayName} Logo`}
                      height={250}
                      width={250}
                      className="mr-1"
                    />
                  ) : (
                    <>
                      <Image
                        src={logo}
                        alt="Salesably Logo"
                        height={32}
                        width={32}
                        className="mr-1"
                      />
                      <h1 className="mb-0 text-2xl font-bold tracking-tight text-gray-900">
                        Salesably
                      </h1>
                    </>
                  )}
                </div>
                <h2 className="text-xl font-medium text-gray-800">
                  Nice to see you again
                </h2>
              </div>

              {message && (
                <p className="mb-6 text-sm text-center text-green-600">
                  {message}
                </p>
              )}
              {error && (
                <p className="mb-6 text-sm text-center text-red-600">{error}</p>
              )}

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label
                    htmlFor="email"
                    className="block pl-2 mb-2 text-sm font-medium text-gray-900"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block pl-2 mb-2 text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>
                  <div className="relative rounded-md shadow-xs">
                    <input
                      id="password"
                      name="password"
                      type={passwordVisible ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      {passwordVisible ? (
                        <EyeOff className="w-5 h-5 text-gray-400" />
                      ) : (
                        <Eye className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label
                    htmlFor="rememberMe"
                    className="relative inline-flex items-center cursor-pointer"
                  >
                    <input
                      id="rememberMe"
                      name="rememberMe"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={() => setRememberMe(!rememberMe)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-hidden peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    <span className="ml-2 text-xs font-medium text-gray-700">
                      Remember me
                    </span>
                  </label>
                  {subdomainParam !== "primrose" && !hostname.startsWith("primrose.") && (
                    <Link
                      href="/forgot-password"
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-500"
                    >
                      Forgot password?
                    </Link>
                  )}
                </div>

                <div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-full"
                  >
                    {loading ? "Signing In..." : "Sign In"}
                  </Button>
                </div>
              </form>

              <div className="my-8 border-t border-gray-300"></div>

              <div className="space-y-4">
                {isRipplingWorkspace && (
                  <button
                    onClick={handleRipplingSignIn}
                    className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                  >
                    <Image
                      src={ripplingLogo}
                      alt="Rippling Logo"
                      height={20}
                      width={20}
                      className="mr-2"
                    />
                    Sign in with Rippling
                  </button>
                )}

                <button
                  onClick={handleGoogleSignIn}
                  className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 border border-gray-300 rounded-full hover:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  <Image
                    src={googleLogo}
                    alt="Google Logo"
                    height={20}
                    width={20}
                    className="mr-2"
                  />
                  Sign in with Google
                </button>

                {showMicrosoftSignIn && (
                  <button
                    onClick={handleMicrosoftSignIn}
                    className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 23 23"
                      className="mr-2"
                    >
                      <path fill="#f3f3f3" d="M0 0h23v23H0z" />
                      <path fill="#f35325" d="M1 1h10v10H1z" />
                      <path fill="#81bc06" d="M12 1h10v10H12z" />
                      <path fill="#05a6f0" d="M1 12h10v10H1z" />
                      <path fill="#ffba08" d="M12 12h10v10H12z" />
                    </svg>
                    Sign in with Microsoft
                  </button>
                )}
              </div>

              <p className="mt-8 text-xs text-center text-gray-600">
                By signing in, I agree to the{" "}
                <Link
                  href="/terms-of-service"
                  className="text-indigo-600 hover:underline"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  href="/privacy-policy"
                  className="text-indigo-600 hover:underline"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
